import React, { useState, useEffect } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { formatDate } from '../../utils/helpers';

// Helper: Monday-based week key — must match Dashboard's getWeekColor logic
const getWeekKey = (iso) => {
  try {
    const d = new Date(iso);
    const dsm = d.getDay() === 0 ? 6 : d.getDay() - 1;
    const monday = new Date(d);
    monday.setDate(d.getDate() - dsm);
    monday.setHours(0, 0, 0, 0);
    return monday.toISOString().slice(0, 10);
  } catch { return 'unknown'; }
};

const daysAgo = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const days = Math.floor(diffMs / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    return `${days}d ago`;
  } catch { return ''; }
};

const LOST_REASONS = [
  'Price',
  'Chose competitor',
  'Pet recovered / no longer needed',
  'Ghosted / stopped responding',
  'Other',
];

const LostReasonModal = ({ prospect, onClose, onSuccess }) => {
  const [reason, setReason] = useState('Price');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await apiClient.post(`/prospects/${prospect.id}/mark-lost`, {
        lost_reason: reason,
        lost_reason_notes: notes || null,
      });
      showToast('Marked as lost');
      onSuccess();
    } catch (e) {
      showToast('Failed to mark lost', 'error');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="lost-reason-modal">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Mark Prospect as Lost</h3>
        <p className="text-sm text-gray-600 mb-4"><span className="font-medium">{prospect.pet_name}</span> · {prospect.client_name}</p>

        <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
        <select value={reason} onChange={e => setReason(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3" data-testid="lost-reason-select">
          {LOST_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
        <textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)}
          placeholder="Any specific detail to remember…"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" data-testid="lost-reason-notes" />

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm" data-testid="lost-cancel">Cancel</button>
          <button onClick={submit} disabled={loading}
            className="px-5 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm font-semibold disabled:opacity-60"
            data-testid="lost-confirm">
            {loading ? 'Saving…' : 'Mark Lost'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ProspectCard = ({ prospect, onEdit, onActivate, onDelete, onMarkLost }) => {
  const statusLabel = {
    new: 'NEW',
    follow_up: 'FOLLOW-UP',
    converted: 'CONVERTED ✓',
    lost: 'LOST',
  }[prospect.status] || 'NEW';

  const isClosed = prospect.status === 'converted' || prospect.status === 'lost';

  // Single dark gray palette — same shell/structure as Active/Completed pet cards
  // but neutral color signals "not yet live"
  const cardShell = isClosed
    ? 'bg-gray-100 border-gray-300 text-gray-600'
    : 'bg-gray-200 border-gray-500 text-gray-900';

  return (
    <div className={`rounded-lg shadow-sm border p-3 relative ${cardShell} ${isClosed ? 'opacity-70' : ''}`}
      data-testid={`prospect-card-${prospect.id}`}>
      {/* Subtle "PROSPECT" corner label */}
      <div className="absolute top-2 right-3 text-[9px] font-bold tracking-wider text-gray-500">
        PROSPECT{prospect.source === 'AI Receptionist' ? ' · AI' : ''}
      </div>

      {/* Header: pet · client · status */}
      <div className="flex items-start justify-between gap-3 pr-20">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-bold text-base">{prospect.pet_name}</h3>
            <span className="text-sm">· {prospect.client_name}</span>
            {prospect.status !== 'new' && (
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                prospect.status === 'converted' ? 'bg-green-100 text-green-800' :
                prospect.status === 'lost' ? 'bg-gray-300 text-gray-700' :
                'bg-blue-100 text-blue-800'
              }`}>
                {statusLabel}
              </span>
            )}
          </div>
          {/* Row 2: contact + pet details */}
          <div className="text-xs mt-1 flex flex-wrap gap-x-3 gap-y-1">
            {prospect.phone_number && <span>📞 {prospect.phone_number}</span>}
            {prospect.pet_type && (
              <span>
                {prospect.pet_type}
                {prospect.breed ? ` · ${prospect.breed}` : ''}
                {prospect.weight_kg ? ` · ${prospect.weight_kg}kg` : ''}
                {prospect.sex ? ` · ${prospect.sex}` : ''}
              </span>
            )}
            {prospect.vet_clinic && <span>🏥 {prospect.vet_clinic}</span>}
            <span className="opacity-75">Created {daysAgo(prospect.created_at)}</span>
          </div>

          {/* Preferences chips */}
          {(prospect.urns_selected?.length > 0 || prospect.keepsakes_selected?.length > 0 || prospect.jewelry_selected?.length > 0 || prospect.transportation_fees) && (
            <div className="text-xs mt-2 flex flex-wrap gap-2">
              {prospect.urns_selected?.length > 0 && (
                <span className="bg-white/60 border border-gray-300 rounded px-2 py-0.5">
                  🏺 {prospect.urns_selected.length} urn{prospect.urns_selected.length > 1 ? 's' : ''}
                </span>
              )}
              {prospect.keepsakes_selected?.length > 0 && (
                <span className="bg-white/60 border border-gray-300 rounded px-2 py-0.5">
                  🎁 {prospect.keepsakes_selected.length} keepsake{prospect.keepsakes_selected.length > 1 ? 's' : ''}
                </span>
              )}
              {prospect.jewelry_selected?.length > 0 && (
                <span className="bg-white/60 border border-gray-300 rounded px-2 py-0.5">
                  💍 {prospect.jewelry_selected.length} jewelry
                </span>
              )}
              {prospect.transportation_fees && (
                <span className="bg-white/60 border border-gray-300 rounded px-2 py-0.5">
                  🚗 {Array.isArray(prospect.transportation_fees) ? prospect.transportation_fees[0] : prospect.transportation_fees}
                </span>
              )}
            </div>
          )}

          {/* Notes in italic bubble (matches pet card style) */}
          {prospect.notes && (
            <div className="text-xs text-gray-700 bg-white/70 border border-gray-300 rounded px-2 py-1 mt-2 italic">
              "{prospect.notes.length > 160 ? prospect.notes.slice(0, 160) + '…' : prospect.notes}"
            </div>
          )}

          {/* Quoted total (replaces payment info) */}
          {prospect.quoted_total != null && (
            <div className="text-xs mt-2">
              <span className="font-semibold">Quoted:</span>{' '}
              <span className="font-bold">${Number(prospect.quoted_total).toFixed(2)}</span>
            </div>
          )}

          {/* Lost reason inline */}
          {prospect.status === 'lost' && prospect.lost_reason && (
            <div className="text-xs mt-2 bg-red-50 border border-red-200 text-red-800 rounded px-2 py-1">
              <span className="font-semibold">Lost:</span> {prospect.lost_reason}
              {prospect.lost_reason_notes && <span className="opacity-80"> — {prospect.lost_reason_notes}</span>}
            </div>
          )}

          {/* Follow-up date */}
          {prospect.follow_up_date && !isClosed && (
            <div className="text-xs mt-1 text-blue-700">
              📅 Follow up: {formatDate(prospect.follow_up_date)}
            </div>
          )}
        </div>
      </div>

      {/* Action buttons (bottom-right, like pet card actions) */}
      {!isClosed && (
        <div className="flex flex-wrap justify-end gap-2 mt-3 pt-3 border-t border-gray-300/60">
          <button onClick={() => onActivate(prospect)}
            className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded-md shadow-sm"
            data-testid={`prospect-activate-${prospect.id}`}>
            ✓ Activate as Pet
          </button>
          <button onClick={() => onEdit(prospect)}
            className="bg-white hover:bg-gray-50 border border-gray-400 text-gray-800 text-xs px-3 py-1.5 rounded-md"
            data-testid={`prospect-edit-${prospect.id}`}>
            ✏️ Edit
          </button>
          <button onClick={() => onMarkLost(prospect)}
            className="bg-white hover:bg-orange-50 border border-orange-300 text-orange-700 text-xs px-3 py-1.5 rounded-md"
            data-testid={`prospect-lost-${prospect.id}`}>
            Mark Lost
          </button>
          <button onClick={() => onDelete(prospect)}
            className="bg-white hover:bg-red-50 border border-red-300 text-red-700 text-xs px-3 py-1.5 rounded-md"
            data-testid={`prospect-delete-${prospect.id}`}>
            🗑 Delete
          </button>
        </div>
      )}
    </div>
  );
};

const ProspectsView = ({ vetClinics = [], onActivate, onCreate, onEdit, onCountChange }) => {
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lostProspect, setLostProspect] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');
  const [search, setSearch] = useState('');
  const [statsExpanded, setStatsExpanded] = useState(() => {
    try { return localStorage.getItem('ps_prospect_stats_expanded') === '1'; } catch { return false; }
  });

  const toggleStats = () => {
    const next = !statsExpanded;
    setStatsExpanded(next);
    try { localStorage.setItem('ps_prospect_stats_expanded', next ? '1' : '0'); } catch {}
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      const [listRes, statsRes] = await Promise.all([
        apiClient.get(`/prospects?${params.toString()}`),
        apiClient.get('/prospects/stats/summary'),
      ]);
      setProspects(listRes.data.prospects || []);
      setStats(statsRes.data);
      // Notify parent so Dashboard tab badge updates
      if (onCountChange) onCountChange();
    } catch (e) {
      console.error('Prospects fetch failed:', e);
      showToast('Failed to load prospects', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [search]);

  const filtered = prospects.filter(p => {
    if (statusFilter === 'active') return p.status === 'new' || p.status === 'follow_up';
    if (statusFilter === 'converted') return p.status === 'converted';
    if (statusFilter === 'lost') return p.status === 'lost';
    return true;
  });

  const handleDelete = async (prospect) => {
    if (!window.confirm(`Delete prospect for ${prospect.pet_name}? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/prospects/${prospect.id}`);
      showToast('Prospect deleted');
      fetchData();
    } catch (e) {
      showToast('Failed to delete', 'error');
    }
  };

  const handleActivate = async (prospect) => {
    try {
      const res = await apiClient.post(`/prospects/${prospect.id}/activate`);
      const prefill = res.data?.prefill;
      if (prefill && onActivate) {
        onActivate(prefill, () => fetchData());
      } else {
        showToast('Could not prefill activation', 'error');
      }
    } catch (e) {
      showToast('Failed to activate', 'error');
    }
  };

  return (
    <div className="px-4 pb-6" data-testid="prospects-view">
      {/* Header + stats */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Prospects (Leads)</h2>
            <p className="text-sm text-gray-600 mt-1">Not-yet-committed enquiries. Activate when the client confirms.</p>
          </div>
          <button onClick={() => { if (onCreate) onCreate(() => fetchData()); }}
            className="bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow"
            data-testid="prospect-new-btn">
            + New Prospect
          </button>
        </div>

        {/* Collapsible KPI stats — hidden by default so staff focus on the list */}
        {stats && (
          <div className="mt-4">
            <button
              onClick={toggleStats}
              className="text-xs font-semibold text-gray-600 hover:text-gray-900 inline-flex items-center gap-1.5"
              data-testid="toggle-prospect-stats">
              <span>{statsExpanded ? '🔽' : '▶️'}</span>
              {statsExpanded ? 'Hide KPIs' : `Show KPIs · ${stats.new + stats.follow_up} active · ${stats.conversion_rate_pct}% conv · $${stats.pipeline_value?.toLocaleString() || 0} pipeline`}
            </button>
            {statsExpanded && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-3">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-xs text-amber-700 font-medium uppercase">Active</p>
                  <p className="text-2xl font-bold text-amber-900" data-testid="stat-active">{stats.new + stats.follow_up}</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-xs text-green-700 font-medium uppercase">Converted</p>
                  <p className="text-2xl font-bold text-green-900" data-testid="stat-converted">{stats.converted}</p>
                </div>
                <div className="bg-gray-100 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs text-gray-700 font-medium uppercase">Lost</p>
                  <p className="text-2xl font-bold text-gray-900" data-testid="stat-lost">{stats.lost}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs text-blue-700 font-medium uppercase">Conv. Rate</p>
                  <p className="text-2xl font-bold text-blue-900" data-testid="stat-conv-rate">{stats.conversion_rate_pct}%</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs text-purple-700 font-medium uppercase">Pipeline $</p>
                  <p className="text-2xl font-bold text-purple-900" data-testid="stat-pipeline">${stats.pipeline_value?.toLocaleString() || 0}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          {[
            { k: 'active', l: `Active (${stats ? stats.new + stats.follow_up : 0})` },
            { k: 'converted', l: `Converted (${stats?.converted || 0})` },
            { k: 'lost', l: `Lost (${stats?.lost || 0})` },
            { k: 'all', l: `All` },
          ].map(b => (
            <button key={b.k}
              onClick={() => setStatusFilter(b.k)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${
                statusFilter === b.k
                  ? 'bg-amber-600 text-white border-amber-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              data-testid={`filter-${b.k}`}>
              {b.l}
            </button>
          ))}
          <input
            type="text"
            placeholder="Search name, phone, email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="ml-auto border border-gray-300 rounded-lg px-3 py-1.5 text-sm w-64"
            data-testid="prospect-search"
          />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-600 mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Loading prospects…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl">
          <p className="text-gray-500 mb-2">No prospects in this view.</p>
          <p className="text-xs text-gray-400">Tip: add a prospect when a caller enquires but hasn't committed yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {(() => {
            // Pre-compute per-week lead counts (only counts visible/filtered prospects)
            const weekCounts = {};
            filtered.forEach(p => {
              const key = getWeekKey(p.created_at);
              weekCounts[key] = (weekCounts[key] || 0) + 1;
            });
            let lastWeekKey = null;
            return filtered.map(p => {
              const weekKey = getWeekKey(p.created_at);
              const isNewWeek = weekKey !== lastWeekKey;
              lastWeekKey = weekKey;
              // Format "Week of 18 Apr"
              const weekLabel = (() => {
                try {
                  const d = new Date(weekKey);
                  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
                } catch { return weekKey; }
              })();
              const count = weekCounts[weekKey];
              return (
                <React.Fragment key={p.id}>
                  {isNewWeek && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center justify-center">
                        <div className="flex-1 h-px bg-blue-200"></div>
                        <span className="px-4 text-sm font-medium text-blue-700">
                          📅 Week of {weekLabel} — {count} {count === 1 ? 'lead' : 'leads'}
                        </span>
                        <div className="flex-1 h-px bg-blue-200"></div>
                      </div>
                    </div>
                  )}
                  <ProspectCard
                    prospect={p}
                    onEdit={(pr) => { if (onEdit) onEdit(pr, () => fetchData()); }}
                    onActivate={handleActivate}
                    onDelete={handleDelete}
                    onMarkLost={(pr) => setLostProspect(pr)}
                  />
                </React.Fragment>
              );
            });
          })()}
        </div>
      )}

      {lostProspect && (
        <LostReasonModal
          prospect={lostProspect}
          onClose={() => setLostProspect(null)}
          onSuccess={() => { setLostProspect(null); fetchData(); }}
        />
      )}
    </div>
  );
};

export default ProspectsView;
